import React, { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { isNotificationEnabled } from "@/utils/notificationPermission";
import api from "@/services/api";

type NotificationPayload = {
  id: string;
  type: string;
  title: string;
  message: string;
  isRead: boolean;
  createdAt: string;
  occurrenceId?: string;
  workOrderId?: string;
  neighborhood?: string | null;
};

type SSEMessage = {
  type: "notification" | "heartbeat";
  payload?: NotificationPayload;
  timestamp?: string;
};

export const useSSENotifications = (enabled: boolean = true) => {
  const [isConnected, setIsConnected] = useState(false);
  const [lastNotification, setLastNotification] = useState<NotificationPayload | null>(null);
  const [hasPermission, setHasPermission] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Verificar permissão de notificação
  useEffect(() => {
    const checkPermission = async () => {
      const permission = await isNotificationEnabled();
      setHasPermission(permission);
    };
    
    checkPermission();
    
    // Verificar periodicamente (a cada 10 segundos) se a permissão mudou
    const interval = setInterval(checkPermission, 10000);
    
    return () => clearInterval(interval);
  }, []);

  // Criar audio element para som de notificação
  useEffect(() => {
    // Usando um som moderno de notificação (Web Audio API)
    audioRef.current = new Audio();
    audioRef.current.volume = 0.5;
    
    // Som de notificação usando data URI (tom moderno e suave)
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const duration = 0.3;
    const sampleRate = audioContext.sampleRate;
    const numSamples = duration * sampleRate;
    const buffer = audioContext.createBuffer(1, numSamples, sampleRate);
    const channelData = buffer.getChannelData(0);
    
    // Gerar tom com envelope ADSR suave
    for (let i = 0; i < numSamples; i++) {
      const t = i / sampleRate;
      const frequency1 = 800; // Nota principal
      const frequency2 = 1200; // Harmônico
      
      // Envelope
      const attack = 0.02;
      const decay = 0.08;
      const sustain = 0.6;
      const release = 0.2;
      
      let envelope = 1;
      if (t < attack) {
        envelope = t / attack;
      } else if (t < attack + decay) {
        envelope = 1 - ((t - attack) / decay) * (1 - sustain);
      } else if (t < duration - release) {
        envelope = sustain;
      } else {
        envelope = sustain * (1 - (t - (duration - release)) / release);
      }
      
      // Duas frequências sobrepostas para som mais rico
      channelData[i] = (
        Math.sin(2 * Math.PI * frequency1 * t) * 0.5 +
        Math.sin(2 * Math.PI * frequency2 * t) * 0.3
      ) * envelope;
    }
    
    // Converter para data URL e usar como fonte
    const offlineContext = new OfflineAudioContext(1, numSamples, sampleRate);
    const source = offlineContext.createBufferSource();
    source.buffer = buffer;
    source.connect(offlineContext.destination);
    source.start();
    
    offlineContext.startRendering().then((renderedBuffer) => {
      // Criar wav file data URI
      const wavData = bufferToWave(renderedBuffer);
      const blob = new Blob([wavData], { type: 'audio/wav' });
      const url = URL.createObjectURL(blob);
      if (audioRef.current) {
        audioRef.current.src = url;
      }
    });

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const playNotificationSound = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch((error) => {
        console.log("Erro ao tocar som de notificação:", error);
      });
    }
  };

  const connect = async () => {
    const token = localStorage.getItem("duas-vidas-token");
    
    // Não conectar se: não tem token, não está habilitado, ou não tem permissão
    if (!token || !enabled || !hasPermission) return;

    try {
      const baseUrl = import.meta.env.VITE_BASE_URL || "http://localhost:3000";
      const url = `${baseUrl}/notifications/stream`;
      
      // Criar AbortController para poder cancelar a conexão
      abortControllerRef.current = new AbortController();
      
      // Usar fetch com ReadableStream para suportar headers customizados
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/event-stream',
        },
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      console.log("✅ SSE conectado");
      setIsConnected(true);

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error("Reader não disponível");
      }

      // Ler o stream
      const readStream = async () => {
        try {
          while (true) {
            const { done, value } = await reader.read();
            
            if (done) {
              console.log("Stream finalizado");
              break;
            }

            // Decodificar chunk
            const chunk = decoder.decode(value, { stream: true });
            
            // Processar linhas do SSE
            const lines = chunk.split('\n');
            
            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const data = line.slice(6); // Remove 'data: '
                
                try {
                  const parsed: SSEMessage = JSON.parse(data);
                  
                  if (parsed.type === "notification" && parsed.payload) {
                    console.log("🔔 Nova notificação:", parsed.payload);
                    setLastNotification(parsed.payload);
                    
                    // Tocar som
                    playNotificationSound();
                    
                    // Função para marcar como lida e navegar
                    const handleNotificationClick = async () => {
                      const occurrenceTypes = ['CITY_HALL_RESPONSE', 'OCCURRENCE_UPDATE', 'OCCURRENCE_STATUS_CHANGE'];
                      const workOrderTypes = ['WORK_ORDER_ASSIGNED', 'WORK_ORDER_UPDATE', 'WORK_ORDER_STATUS_CHANGE'];
                      
                      // Marcar como lida
                      try {
                        await api.patch("/notifications/mark-as-read", {
                          notificationIds: [parsed.payload!.id],
                        });
                        queryClient.invalidateQueries({ queryKey: ["notifications"] });
                        queryClient.invalidateQueries({ queryKey: ["infiniteNotifications"] });
                        queryClient.invalidateQueries({ queryKey: ["unreadCount"] });
                      } catch (error) {
                        console.error("Erro ao marcar notificação como lida:", error);
                      }
                      
                      const type = parsed.payload?.type || '';
                      if (occurrenceTypes.includes(type) && parsed.payload?.occurrenceId) {
                        navigate(`/ocurrences/details/${parsed.payload.occurrenceId}`);
                      } else if (workOrderTypes.includes(type) && parsed.payload?.workOrderId) {
                        navigate(`/work-orders/${parsed.payload.workOrderId}`);
                      }
                    };
                    
                    // Mostrar toast customizado
                    const occurrenceClickable = ['CITY_HALL_RESPONSE', 'OCCURRENCE_UPDATE', 'OCCURRENCE_STATUS_CHANGE'].includes(parsed.payload?.type || '') && !!parsed.payload?.occurrenceId;
                    const workOrderClickable = ['WORK_ORDER_ASSIGNED', 'WORK_ORDER_UPDATE', 'WORK_ORDER_STATUS_CHANGE'].includes(parsed.payload?.type || '') && !!parsed.payload?.workOrderId;
                    const isClickable = occurrenceClickable || workOrderClickable;
                    
                    toast.custom(
                      (t) => React.createElement(
                        'div',
                        { 
                          className: `flex items-start gap-3 bg-white p-4 rounded-xl shadow-lg border border-gray-100 max-w-md ${
                            isClickable ? 'cursor-pointer hover:shadow-xl transition-shadow' : ''
                          }`,
                          style: { minWidth: '300px' },
                          onClick: isClickable ? () => {
                            toast.dismiss(t.id);
                            handleNotificationClick();
                          } : undefined
                        },
                        React.createElement('div', { 
                          className: 'w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0' 
                        }, '🔔'),
                        React.createElement('div', { className: 'flex-1 min-w-0' },
                          React.createElement('p', { 
                            className: 'font-semibold text-gray-900 text-sm mb-1' 
                          }, parsed.payload?.title || ''),
                          React.createElement('p', { 
                            className: 'text-sm text-gray-600' 
                          }, parsed.payload?.message || ''),
                          isClickable ? React.createElement('p', {
                            className: 'text-xs text-primary mt-1 font-medium'
                          }, 'Clique para ver detalhes') : null
                        ),
                        React.createElement('button', {
                          onClick: (e: any) => {
                            e.stopPropagation();
                            toast.dismiss(t.id);
                          },
                          className: 'text-gray-400 hover:text-gray-600 text-lg'
                        }, '✕')
                      ),
                      {
                        duration: 6000,
                        position: 'top-right',
                      }
                    );
                    
                    // Invalidar cache de notificações e count
                    queryClient.invalidateQueries({ queryKey: ["notifications"] });
                    queryClient.invalidateQueries({ queryKey: ["unreadCount"] });
                  } else if (parsed.type === "heartbeat") {
                    console.log("💓 Heartbeat recebido");
                  }
                } catch (error) {
                  // Ignorar linhas que não são JSON válido
                }
              }
            }
          }
        } catch (error) {
          if ((error as Error).name !== 'AbortError') {
            console.error("Erro ao ler stream:", error);
            throw error;
          }
        }
      };

      await readStream();

    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        console.error("❌ Erro na conexão SSE:", error);
        setIsConnected(false);
        
        // Tentar reconectar após 5 segundos
        if (enabled) {
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log("🔄 Tentando reconectar...");
            connect();
          }, 5000);
        }
      }
    }
  };

  const disconnect = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    setIsConnected(false);
  };

  useEffect(() => {
    // Só conecta se habilitado E tem permissão
    if (enabled && hasPermission) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [enabled, hasPermission]);

  return {
    isConnected,
    lastNotification,
    reconnect: connect,
    disconnect,
  };
};

// Helper function to convert AudioBuffer to WAV
function bufferToWave(abuffer: AudioBuffer): ArrayBuffer {
  const numOfChan = abuffer.numberOfChannels;
  const length = abuffer.length * numOfChan * 2 + 44;
  const buffer = new ArrayBuffer(length);
  const view = new DataView(buffer);
  const channels = [];
  let offset = 0;
  let pos = 0;

  // Write WAV header
  setUint32(0x46464952); // "RIFF"
  setUint32(length - 8); // file length - 8
  setUint32(0x45564157); // "WAVE"
  setUint32(0x20746d66); // "fmt " chunk
  setUint32(16); // length = 16
  setUint16(1); // PCM (uncompressed)
  setUint16(numOfChan);
  setUint32(abuffer.sampleRate);
  setUint32(abuffer.sampleRate * 2 * numOfChan); // avg. bytes/sec
  setUint16(numOfChan * 2); // block-align
  setUint16(16); // 16-bit
  setUint32(0x61746164); // "data" - chunk
  setUint32(length - pos - 4); // chunk length

  // Write interleaved data
  for (let i = 0; i < abuffer.numberOfChannels; i++) {
    channels.push(abuffer.getChannelData(i));
  }

  while (pos < length) {
    for (let i = 0; i < numOfChan; i++) {
      let sample = Math.max(-1, Math.min(1, channels[i][offset]));
      sample = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
      view.setInt16(pos, sample, true);
      pos += 2;
    }
    offset++;
  }

  return buffer;

  function setUint16(data: number) {
    view.setUint16(pos, data, true);
    pos += 2;
  }

  function setUint32(data: number) {
    view.setUint32(pos, data, true);
    pos += 4;
  }
}
